import React, { useState, useRef } from "react";
import { Alert, Badge, Button, Container, Form, Spinner, Table } from "react-bootstrap";
import axios from "../utils/api";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import "../styles/InvoiceUpload.css";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";
const PREVIEW_LIMIT = 100;

const IMPORT_TYPES = [
  { value: "transactions", label: "POS Transactions", endpoint: "api/pos/import", description: "Sales basket data (Basket #, UPC, Qty, Amount…)" },
  { value: "pricebook",    label: "Pricebook",         endpoint: "api/pricebook/import", description: "Item catalog (UPC, Name, Price, Department…)" },
];

function PosImport() {
  const [importType, setImportType] = useState("transactions");
  const [file, setFile] = useState(null);
  const [records, setRecords] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const importLockRef = useRef(false);
  const [error, setError] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const currentType = IMPORT_TYPES.find((t) => t.value === importType);

  const handleTypeChange = (val) => {
    setImportType(val);
    setFile(null);
    setRecords([]);
    setColumns([]);
    setError(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setFile(selected);
    setError(null);
    setRecords([]);
    setColumns([]);
    setImportResult(null);
  };

  // Normalize a dollar string like "$1,234.56" → 1234.56, or "" → null
  const parseDollar = (val) => {
    if (val === null || val === undefined || val === "") return null;
    const n = parseFloat(String(val).replace(/[$,]/g, ""));
    return isNaN(n) ? null : n;
  };

  // Map the known POS basket export columns to clean field names
  const normalizePosRow = (row) => {
    // Support both the known header names and already-normalized names
    return {
      entered:      row["Entered"]       || row["entered"]       || "",
      basket_id:    row["Basket #"]      || row["basket_id"]     || "",
      dept:         row["Dept"]          || row["dept"]          || "",
      upc:          row["UPC/PLU"]       || row["upc"]           || "",
      item:         row["Item"]          || row["item"]          || "",
      qty:          parseFloat(row["Qty."] ?? row["qty"] ?? "") || null,
      amount:       parseDollar(row["Amt $"]      ?? row["amount"]),
      discount:     parseDollar(row["Discount"]   ?? row["discount"]),
      promo:        row["Promo"]         || row["promo"]         || "",
      verified_age: row["Verified Age"]  || row["verified_age"]  || "",
      ebt_sales:    parseDollar(row["EBT Sales $"] ?? row["ebt_sales"]),
    };
  };

  const handleParse = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }
    setLoading(true);
    setError(null);
    setRecords([]);
    setColumns([]);
    setImportResult(null);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const raw = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      if (!raw || raw.length === 0) throw new Error("No data found in the file");

      // Detect if this is the known basket format by checking for "Basket #" or "Entered"
      const firstKeys = Object.keys(raw[0]);
      const isBasketFormat = firstKeys.some((k) =>
        ["Basket #", "Entered", "UPC/PLU", "Amt $"].includes(k)
      );

      const normalized = isBasketFormat ? raw.map(normalizePosRow) : raw;

      setColumns(Object.keys(normalized[0]));
      setRecords(normalized);
      toast.success(`Parsed ${normalized.length.toLocaleString()} records`);
    } catch (err) {
      setError(err.message || "Failed to parse file");
      toast.error("Parse failed: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (records.length === 0) return;
    if (importLockRef.current) return;          // immediate guard
    importLockRef.current = true;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const response = await axios.post(`${API_URL}${currentType.endpoint}`, {
        records,
        fileName: file?.name || "",
      });
      if (!response.data?.success) throw new Error(response.data?.message || "Import failed");
      setImportResult(response.data);
      const d = response.data;
      const parts = [`Inserted ${(d.inserted || 0).toLocaleString()} records`];
      if (d.skippedBaskets) parts.push(`skipped ${d.skippedBaskets} duplicate basket(s)`);
      toast.success(parts.join(", "));
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || "Import failed";
      setError(msg);
      toast.error("Import failed: " + msg);
    } finally {
      setImporting(false);
      importLockRef.current = false;
    }
  };

  const handleClear = () => {
    setFile(null);
    setRecords([]);
    setColumns([]);
    setError(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Container className="invoice-upload-container mt-4">
      <h2 className="mb-4">Import Data</h2>

      {/* Import type selector */}
      <div className="upload-section card p-4 mb-3">
        <h5 className="mb-3">What are you importing?</h5>
        <div className="d-flex gap-3">
          {IMPORT_TYPES.map((t) => (
            <div
              key={t.value}
              className={`border rounded p-3 flex-fill cursor-pointer ${importType === t.value ? "border-primary bg-primary bg-opacity-10" : "border-secondary"}`}
              style={{ cursor: "pointer" }}
              onClick={() => handleTypeChange(t.value)}
            >
              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="radio"
                  name="importType"
                  id={`type-${t.value}`}
                  checked={importType === t.value}
                  onChange={() => handleTypeChange(t.value)}
                  label=""
                  className="mb-0"
                />
                <div>
                  <strong>{t.label}</strong>
                  <div className="text-muted small">{t.description}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="upload-section card p-4 mb-4">
        <h5 className="mb-3">Select File</h5>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>CSV or Excel file (.csv, .xlsx, .xls)</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              disabled={loading || importing}
            />
            {file && (
              <Form.Text className="text-muted">
                {file.name} &mdash; {(file.size / 1024).toFixed(1)} KB
              </Form.Text>
            )}
          </Form.Group>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="button-group">
            <Button
              variant="primary"
              onClick={handleParse}
              disabled={!file || loading || importing}
              className="me-2"
            >
              {loading ? (
                <><Spinner animation="border" size="sm" className="me-2" />Parsing...</>
              ) : "Parse File"}
            </Button>

            <Button
              variant="success"
              onClick={handleImport}
              disabled={records.length === 0 || importing || loading}
              className="me-2"
            >
              {importing ? (
                <><Spinner animation="border" size="sm" className="me-2" />Importing...</>
              ) : (
                records.length > 0
                  ? `Import ${records.length.toLocaleString()} ${currentType.label} Records`
                  : "Import"
              )}
            </Button>

            <Button
              variant="outline-danger"
              onClick={handleClear}
              disabled={!file && records.length === 0}
            >
              Clear
            </Button>
          </div>
        </Form>
      </div>

      {importResult && (
        <Alert variant={importResult.skippedBaskets ? "warning" : "success"} className="mb-4">
          <strong>Import complete!</strong>{" "}
          <Badge bg="success">{(importResult.inserted || 0).toLocaleString()}</Badge> records inserted
          {importResult.newBaskets != null && <> ({importResult.newBaskets} new basket{importResult.newBaskets !== 1 ? "s" : ""})</>}
          {importResult.skippedBaskets > 0 && (
            <> &nbsp;|&nbsp; <Badge bg="secondary">{importResult.skippedBaskets}</Badge> duplicate basket{importResult.skippedBaskets !== 1 ? "s" : ""} skipped ({(importResult.skippedRows || 0).toLocaleString()} rows)</>
          )}
          {" "}from <strong>{file?.name}</strong>
          {importResult.uploadedAt && (
            <> at {new Date(importResult.uploadedAt).toLocaleString()}</>
          )}.
        </Alert>
      )}

      {records.length > 0 && (
        <div className="table-section card p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">
              Preview{" "}
              <Badge bg="secondary">
                {records.length > PREVIEW_LIMIT
                  ? `${PREVIEW_LIMIT} of ${records.length.toLocaleString()}`
                  : records.length.toLocaleString()}{" "}
                rows
              </Badge>
            </h5>
            <span className="text-muted small">{columns.length} columns</span>
          </div>

          <div className="table-responsive sticky-table sticky-table--inner">
            <Table striped bordered hover size="sm">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  {columns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.slice(0, PREVIEW_LIMIT).map((row, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    {columns.map((col) => (
                      <td key={`${idx}-${col}`} className="text-nowrap">
                        {row[col] !== "" ? String(row[col]) : <span className="text-muted">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {records.length > PREVIEW_LIMIT && (
            <p className="text-muted small mt-2 mb-0">
              Showing first {PREVIEW_LIMIT} of {records.length.toLocaleString()} rows — all rows will be imported.
            </p>
          )}
        </div>
      )}
    </Container>
  );
}

export default PosImport;
